// ============================================================================
// DynamicForm — the orchestrator
// ============================================================================
// Reads a FormSpec + initial FormValues, renders each visible field via
// <DynamicField /> (or <DynamicGrid /> for grids), tracks form state, runs
// the validation engine on save, and hands the validated values to onSave.
//
// Does NOT know about case_state_data — the parent (DynamicStateSection)
// owns persistence. This keeps DynamicForm reusable for any future schema.
//
// Layout: when spec.sections is present, fields are grouped into titled
// sub-panels. Otherwise the flat spec.fields list is rendered as a single
// section.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import {
  collectAllFields,
  type FieldSpec,
  type FormSpec,
  type FormValues,
  type SectionSpec,
} from 'api/types/formSpec.types';
import DynamicField from './DynamicField';
import DynamicGrid from './DynamicGrid';
import NoticeSection from './NoticeSection';
import CaseStatusSection from './CaseStatusSection';
import { resolveEmbeddedSection } from './embeddedSectionRegistry';
import { evalBool } from './expressionEvaluator';
import { evalComputed } from './computeField';
import type { SeedContext } from './seedResolver';
import { runValidations, type ValidationResult } from './validationEngine';

export interface DynamicFormProps {
  spec: FormSpec | null;
  initial?: FormValues;
  /** Disables every field — used for past-state read-only mode. */
  readOnly?: boolean;
  /** Hides the action footer — caller renders Save / Cancel themselves. */
  hideFooter?: boolean;
  /** Called when the user clicks Save with valid values (computed fields
   *  already merged in). */
  onSave?: (values: FormValues) => Promise<void> | void;
  /** Called when the user clicks Cancel. */
  onCancel?: () => void;
  /** Optional save-confirmation step (matches existing CRUD pattern). */
  confirmOnSave?: boolean;
  /** Saving label override (default 'Save'). */
  saveLabel?: string;
  /** Opaque context passed to grid seed resolvers (e.g. `{ caseId }`). */
  seedContext?: SeedContext;
}

const DynamicForm: React.FC<DynamicFormProps> = ({
  spec, initial, readOnly, hideFooter, onSave, onCancel,
  confirmOnSave = false, saveLabel = 'Save', seedContext,
}) => {
  const [values, setValues] = useState<FormValues>(initial ?? {});
  const initialSnapshotRef = useRef(JSON.stringify(initial ?? {}));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [warnings, setWarnings] = useState<ValidationResult['warnings']>([]);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const isDirty = useMemo(
    () => JSON.stringify(values) !== initialSnapshotRef.current,
    [values],
  );

  // Re-compute every `computed` field on each value change. Keeps the UI in
  // sync with derived values (cooling-off expiry, etc.) while the user types.
  const allFields = useMemo<FieldSpec[]>(() => spec ? collectAllFields(spec) : [], [spec]);
  useEffect(() => {
    if (!spec) return;
    let changed = false;
    const next = { ...values };
    for (const f of allFields) {
      if (!f.computed) continue;
      const derived = evalComputed(f.computed, next);
      if (next[f.key] !== derived) { next[f.key] = derived; changed = true; }
    }
    if (changed) setValues(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(values), spec]);

  const updateField = useCallback((key: string, val: unknown) => {
    setValues((prev) => ({ ...prev, [key]: val }));
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  // Sections: explicit sections[] or a single anonymous section wrapping fields[].
  const sections = useMemo<SectionSpec[]>(() => {
    if (!spec) return [];
    if (spec.sections && spec.sections.length > 0) return spec.sections;
    return [{ key: '__root', title: '', fields: spec.fields ?? [] }];
  }, [spec]);

  // Tabbed mode — group sections into tabs (Filing stage: Arbitration Details /
  // Accounts & Respondents / Hearings). Sections sharing a `tab` value stack in
  // one tab; tab identity falls back to the section key.
  const tabOf = (s: SectionSpec): string => s.tab ?? s.key;
  const tabs = useMemo<Array<{ id: string; label: string }>>(() => {
    const seen = new Map<string, { id: string; label: string }>();
    for (const s of sections) {
      const id = tabOf(s);
      if (!seen.has(id)) seen.set(id, { id, label: s.tab_label ?? s.title ?? id });
    }
    return [...seen.values()];
  }, [sections]);

  const tabbed = !!spec?.tabbed && tabs.length > 1;
  const [activeTabKey, setActiveTabKey] = useState<string | null>(null);
  useEffect(() => {
    if (!tabbed) return;
    if (activeTabKey && tabs.some((t) => t.id === activeTabKey)) return;
    setActiveTabKey(tabs[0]?.id ?? null);
  }, [tabbed, tabs, activeTabKey]);

  const handleSaveClick = useCallback(() => {
    if (!spec) return;
    const result = runValidations(spec, values);
    setErrors(result.fieldErrors);
    setWarnings(result.warnings);

    if (!result.canSave) {
      const firstKey = Object.keys(result.fieldErrors).find((k) => !k.startsWith('__rule_'));
      // In tabbed mode, jump to the tab holding the first error so the user can see it.
      if (firstKey && tabbed) {
        const sec = sections.find((s) => (s.fields ?? []).some((f) => f.key === firstKey));
        if (sec) setActiveTabKey(sec.tab ?? sec.key);
      }
      requestAnimationFrame(() => {
        if (firstKey) {
          const el = document.querySelector<HTMLElement>(`[data-field="${firstKey}"]`);
          el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
      return;
    }

    if (confirmOnSave) {
      setSaveConfirmOpen(true);
    } else {
      void doSave();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spec, values, confirmOnSave, tabbed, sections]);

  const doSave = useCallback(async () => {
    if (!onSave) return;
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      await onSave(values);
      initialSnapshotRef.current = JSON.stringify(values);
      notificationService.toast.success('Saved successfully.');
    } catch (err) {
      notificationService.toast.error(
        err instanceof Error ? err.message : 'Save failed. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }, [onSave, values]);

  if (!spec) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
        <IconSystem.info size={32} style={{ opacity: 0.3, marginBottom: '0.75rem' }} />
        <div style={{ fontWeight: 500 }}>No form configured for this step</div>
      </div>
    );
  }

  const ruleErrors = Object.entries(errors).filter(([k]) => k.startsWith('__rule_'));
  const noVisibleFields = allFields.every((f) =>
    f.show_if ? !evalBool(f.show_if, values, true) : false,
  );

  // Sections visible in the active tab (all sections when not tabbed).
  const activeSections = tabbed ? sections.filter((s) => tabOf(s) === activeTabKey) : sections;
  // Hide the form Save when the active tab has nothing to save through the form
  // (embedded self-persisting tabs like Accounts & Respondents / Case Details).
  const activeHasSavable = activeSections.some((s) => (s.fields ?? []).length > 0);

  // Render one section — dispatches reusable widgets, else the generic grid.
  const renderSection = (section: SectionSpec, idx: number): React.ReactNode => {
    if (section.render_as === 'notice') {
      return (
        <div key={section.key} style={{ marginBottom: '0.9rem' }}>
          <NoticeSection
            section={section} values={values} onFieldChange={updateField}
            errors={errors} disabled={readOnly || isSaving} seedContext={seedContext}
          />
        </div>
      );
    }
    if (section.render_as === 'case_status') {
      return (
        <div key={section.key} style={{ marginBottom: '0.9rem' }}>
          <CaseStatusSection
            section={section} values={values} onFieldChange={updateField}
            errors={errors} disabled={readOnly || isSaving} seedContext={seedContext}
          />
        </div>
      );
    }
    if (section.render_as === 'accounts' || section.render_as === 'respondents' || section.render_as === 'case_details') {
      const Comp = resolveEmbeddedSection(section.render_as);
      if (!Comp) {
        return (
          <div key={section.key} style={{ padding: '1rem', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
            {section.title ?? section.render_as} — component not registered.
          </div>
        );
      }
      const cfg =
        (section.render_as === 'accounts' ? section.accounts
          : section.render_as === 'respondents' ? section.respondents
          : section.case_details) ?? {};
      return (
        <div key={section.key} style={{ marginBottom: '0.9rem' }}>
          <Comp seedContext={seedContext} config={cfg} disabled={readOnly || isSaving} />
        </div>
      );
    }

    const visible = (section.fields ?? []).filter((f) =>
      !f.show_if || evalBool(f.show_if, values, true)
    );
    if (visible.length === 0) return null;

    const layout = section.layout ?? spec.layout;
    const columns =
      layout === 'single'  ? '1fr' :
      layout === 'two_col' ? 'repeat(2, minmax(0, 1fr))' :
                             'repeat(auto-fit, minmax(220px, 1fr))';

    // In tabbed mode the tab strip already names the section — skip the header.
    const showHeader = !tabbed && (section.title || section.description);
    const isFirst = idx === 0;

    return (
      <div key={section.key} style={{ marginBottom: '0.9rem' }}>
        {showHeader && (
          <div style={{
            marginTop: isFirst ? 0 : '0.4rem',
            marginBottom: '0.45rem',
            paddingBottom: '0.3rem',
            borderBottom: '1px solid var(--border-color)',
          }}>
            {section.title && (
              <div style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                {section.title}
              </div>
            )}
            {section.description && (
              <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '0.15rem' }}>
                {section.description}
              </div>
            )}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: columns, gap: '0.6rem 0.85rem' }}>
          {visible.map((field) => {
            if (field.type === 'grid') {
              return (
                <div key={field.key} style={{ gridColumn: '1 / -1' }}>
                  <DynamicGrid
                    field={field}
                    value={values[field.key] as any}
                    onChange={(next) => updateField(field.key, next)}
                    error={errors[field.key]}
                    disabled={readOnly || isSaving}
                    seedContext={seedContext}
                  />
                </div>
              );
            }
            return (
              <DynamicField
                key={field.key}
                field={field}
                value={values[field.key]}
                onChange={(v) => updateField(field.key, v)}
                error={errors[field.key]}
                disabled={readOnly || isSaving || !!field.computed || !!field.readonly}
                allValues={values}
                seedContext={seedContext}
              />
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '0.25rem 0' }} aria-busy={isSaving}>
      {spec.description && (
        <div style={{
          padding: '0.5rem 0.75rem', marginBottom: '0.75rem',
          background: 'var(--bg-secondary)', borderLeft: '3px solid #2563eb',
          fontSize: '0.78rem', color: 'var(--text-primary)', borderRadius: 4,
        }}>
          {spec.description}
        </div>
      )}

      {ruleErrors.length > 0 && (
        <div style={{
          padding: '0.5rem 0.75rem', marginBottom: '0.75rem',
          background: '#fef2f2', borderLeft: '3px solid #dc2626',
          fontSize: '0.78rem', color: '#7f1d1d', borderRadius: 4,
        }}>
          {ruleErrors.map(([k, msg]) => <div key={k}>{msg}</div>)}
        </div>
      )}

      {warnings.length > 0 && (
        <div style={{
          padding: '0.5rem 0.75rem', marginBottom: '0.75rem',
          background: '#fffbeb', borderLeft: '3px solid #d97706',
          fontSize: '0.78rem', color: '#78350f', borderRadius: 4,
        }}>
          {warnings.map((w) => <div key={w.id}>⚠ {w.message}</div>)}
        </div>
      )}

      {tabbed && (
        <FormTabStrip
          tabs={tabs}
          activeKey={activeTabKey}
          onSelect={setActiveTabKey}
        />
      )}

      {activeSections.map((section) => renderSection(section, sections.indexOf(section)))}

      {noVisibleFields && allFields.length === 0 && (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
          This step has no data fields — advance via the action bar.
        </div>
      )}

      {!hideFooter && onSave && !readOnly && activeHasSavable && (
        <div style={{
          marginTop: '1rem', paddingTop: '0.75rem',
          borderTop: '1px solid var(--border-color)',
          display: 'flex', justifyContent: 'flex-end', gap: '0.5rem',
        }}>
          {onCancel && (
            <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={isSaving}>
              Cancel
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveClick}
            disabled={isSaving || !isDirty}
          >
            {isSaving ? 'Saving…' : saveLabel}
          </button>
        </div>
      )}

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Save"
        message="Save the changes to this step?"
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        loading={isSaving}
        loadingMessage="Saving…"
        onConfirm={() => void doSave()}
        onCancel={() => setSaveConfirmOpen(false)}
      />
    </div>
  );
};

// ----------------------------------------------------------------------------
// FormTabStrip — one tab per section (tabbed FormSpec, e.g. the Filing stage)
// ----------------------------------------------------------------------------

const FormTabStrip: React.FC<{
  tabs: Array<{ id: string; label: string }>;
  activeKey: string | null;
  onSelect: (key: string) => void;
}> = ({ tabs, activeKey, onSelect }) => (
  <div style={{
    display: 'flex', flexWrap: 'wrap', gap: '0.25rem',
    borderBottom: '1px solid var(--border-color)', marginBottom: '0.85rem',
  }}>
    {tabs.map((t) => {
      const isActive = activeKey === t.id;
      return (
        <button
          key={t.id}
          type="button"
          onClick={() => onSelect(t.id)}
          style={{
            padding: '0.5rem 0.85rem',
            fontSize: '0.82rem',
            fontWeight: isActive ? 600 : 500,
            color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
            background: isActive ? 'var(--bg-primary)' : 'transparent',
            border: 'none',
            borderBottom: isActive ? '2px solid #2563eb' : '2px solid transparent',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {t.label}
        </button>
      );
    })}
  </div>
);

export default DynamicForm;
